import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import PaymentForm from '../components/Forms/PaymentForm';
import { Plus, X, Banknote, TrendingUp, Users, Trophy, Zap, Flame, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const CLIENT_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-500', 'bg-sky-500'];

const Payments = () => {
    const { currency } = useAuth();
    const [payments, setPayments] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [paymentsRes, customersRes] = await Promise.all([
                api.get('/payments'),
                api.get('/customers?limit=100')
            ]);
            setPayments(paymentsRes.data);
            setCustomers(customersRes.data.customers);
        } catch (err) {
            console.error('Error fetching data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const stats = useMemo(() => {
        const total = payments.reduce((s, p) => s + (p.amount || 0), 0);

        const now = new Date();
        const thisMonthPayments = payments.filter(p => {
            const d = new Date(p.paymentDate);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
        const thisMonth = thisMonthPayments.reduce((s, p) => s + (p.amount || 0), 0);

        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthPayments = payments.filter(p => {
            const d = new Date(p.paymentDate);
            return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
        });
        const lastMonth = lastMonthPayments.reduce((s, p) => s + (p.amount || 0), 0);
        const growth = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : 0;

        // Per-client breakdown
        const clientMap: Record<string, { name: string; amount: number; count: number }> = {};
        payments.forEach(p => {
            const name = p.customer?.displayName || 'Unknown';
            const id = p.customerId || name;
            if (!clientMap[id]) clientMap[id] = { name, amount: 0, count: 0 };
            clientMap[id].amount += p.amount || 0;
            clientMap[id].count++;
        });
        const clientBreakdown = Object.values(clientMap).sort((a, b) => b.amount - a.amount);
        const topClient = clientBreakdown[0];
        const topPct = total > 0 && topClient ? Math.round((topClient.amount / total) * 100) : 0;
        const thisMonthPct = total > 0 ? Math.round((thisMonth / total) * 100) : 0;

        return { total, thisMonth, lastMonth, growth, clientBreakdown, topClient, topPct, thisMonthPct, count: payments.length, clientCount: clientBreakdown.length };
    }, [payments]);

    const filteredPayments = useMemo(() => {
        return payments.filter((p: any) => {
            if (filterCustomer && p.customerId !== filterCustomer) return false;
            const date = new Date(p.paymentDate);
            if (filterDateFrom && date < new Date(filterDateFrom)) return false;
            if (filterDateTo && date > new Date(filterDateTo + 'T23:59:59')) return false;
            return true;
        });
    }, [payments, filterCustomer, filterDateFrom, filterDateTo]);

    const hasFilters = filterCustomer || filterDateFrom || filterDateTo;

    const bannerColor = stats.thisMonth > stats.lastMonth && stats.lastMonth > 0
        ? 'from-emerald-500 to-teal-600'
        : stats.count === 0
            ? 'from-gray-500 to-gray-600'
            : stats.thisMonth > 0
                ? 'from-blue-600 to-indigo-600'
                : 'from-amber-500 to-orange-500';

    const BannerIcon = stats.thisMonth > stats.lastMonth && stats.lastMonth > 0 ? TrendingUp : stats.thisMonth > 0 ? Zap : stats.count === 0 ? ShieldCheck : Flame;

    const bannerMsg = stats.count === 0
        ? 'No payments logged yet — start billing!'
        : stats.thisMonth > stats.lastMonth && stats.lastMonth > 0
            ? `Up ${Math.abs(stats.growth).toFixed(0)}% vs last month — great momentum!`
            : stats.thisMonth < stats.lastMonth && stats.lastMonth > 0
                ? `Down ${Math.abs(stats.growth).toFixed(0)}% vs last month — chase those invoices!`
                : `${stats.count} payments from ${stats.clientCount} client${stats.clientCount !== 1 ? 's' : ''}`;

    const handleCreate = () => { setEditingPayment(null); setShowForm(true); };
    const handleEdit = (payment: any) => { setEditingPayment(payment); setShowForm(true); };
    const handleDelete = async (payment: any) => {
        if (window.confirm('Are you sure you want to delete this payment?')) {
            try {
                await api.delete(`/payments/${payment.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting payment', err);
            }
        }
    };

    const columns = [
        { header: 'Date', accessor: (item: any) => new Date(item.paymentDate).toLocaleDateString() },
        { header: 'Payment #', accessor: 'paymentNumber' },
        { header: 'Customer Name', accessor: (item: any) => item.customer?.displayName || 'Unknown' },
        { header: 'Project Name', accessor: (item: any) => item.project?.projectName || '-' },
        { header: 'Payment Mode', accessor: 'paymentMode' },
        { header: 'Amount', accessor: (item: any) => formatCurrency(item.amount, currency) },
    ];

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Payments Received</h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm"
                >
                    <Plus size={18} />
                    New
                </button>
            </div>

            {/* Motivational Banner */}
            <div className={`relative bg-gradient-to-r ${bannerColor} rounded-xl p-5 text-white overflow-hidden`}>
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BannerIcon size={16} className="text-white/90" />
                            <p className="text-white/90 text-sm font-medium">{bannerMsg}</p>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(stats.total, currency)} <span className="text-base font-normal text-white/80">total received</span></p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-white/80 flex-wrap">
                            <span>{stats.clientCount} client{stats.clientCount !== 1 ? 's' : ''}</span>
                            {stats.thisMonth > 0 && <span className="font-semibold text-white">{formatCurrency(stats.thisMonth, currency)} this month</span>}
                            {stats.topClient && <span>Top: <span className="font-semibold text-white">{stats.topClient.name}</span> ({stats.topPct}%)</span>}
                        </div>
                    </div>
                    {/* This month ring */}
                    <div className="flex flex-col items-center">
                        <div className="relative w-16 h-16">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60">
                                <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                                <circle cx="30" cy="30" r="24" fill="none" stroke="white" strokeWidth="6"
                                    strokeDasharray={`${2 * Math.PI * 24}`}
                                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - stats.thisMonthPct / 100)}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">{stats.thisMonthPct}%</div>
                        </div>
                        <p className="text-xs text-white/70 mt-1">This Month</p>
                    </div>
                </div>
                <div className="relative mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${stats.thisMonthPct}%` }} />
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <Banknote size={16} className="text-emerald-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Total Received</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">{formatCurrency(stats.total, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.count} payment{stats.count !== 1 ? 's' : ''}</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <TrendingUp size={16} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">This Month</span>
                    </div>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(stats.thisMonth, currency)}</p>
                    <p className="text-xs mt-0.5">
                        {stats.lastMonth > 0
                            ? <span className={stats.growth >= 0 ? 'text-emerald-500' : 'text-red-400'}>{stats.growth >= 0 ? '▲' : '▼'} {Math.abs(stats.growth).toFixed(0)}% vs last month</span>
                            : <span className="text-gray-400">No data last month</span>}
                    </p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                            <Users size={16} className="text-violet-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Clients</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">{stats.clientCount}</p>
                    <p className="text-xs text-gray-400 mt-0.5">paying clients</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Trophy size={16} className="text-amber-500" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Top Client</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800 truncate">{stats.topClient?.name || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.topClient ? `${formatCurrency(stats.topClient.amount, currency)} · ${stats.topPct}%` : 'No payments yet'}</p>
                </div>
            </div>

            {/* Client breakdown + table */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {stats.clientBreakdown.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm md:col-span-1">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">By Client</h3>
                        <div className="space-y-3">
                            {stats.clientBreakdown.slice(0, 6).map((client, i) => {
                                const pct = stats.total > 0 ? Math.round((client.amount / stats.total) * 100) : 0;
                                return (
                                    <div key={client.name}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-gray-600 truncate">{client.name}</span>
                                            <span className="text-xs text-gray-500 ml-1 shrink-0">{pct}%</span>
                                        </div>
                                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div className={`h-full rounded-full transition-all duration-700 ${CLIENT_COLORS[i % CLIENT_COLORS.length]}`} style={{ width: `${pct}%` }} />
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{formatCurrency(client.amount, currency)}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className={stats.clientBreakdown.length > 0 ? 'md:col-span-3' : 'md:col-span-4'}>
                    {/* Filters */}
                    <div className="flex flex-wrap gap-3 mb-3 items-center">
                        <select
                            value={filterCustomer}
                            onChange={e => setFilterCustomer(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                        >
                            <option value="">All Clients</option>
                            {customers.map((c: any) => (
                                <option key={c.id} value={c.id}>{c.displayName}</option>
                            ))}
                        </select>
                        <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        {hasFilters && (
                            <button onClick={() => { setFilterCustomer(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition">
                                <X size={14} /> Clear
                            </button>
                        )}
                        <span className="ml-auto text-sm text-gray-500">{filteredPayments.length} result{filteredPayments.length !== 1 ? 's' : ''}</span>
                    </div>

                    <DataTable
                        data={filteredPayments}
                        columns={columns}
                        total={filteredPayments.length}
                        page={1}
                        limit={100}
                        onPageChange={() => { }}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        loading={loading}
                    />
                </div>
            </div>

            {showForm && (
                <PaymentForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => { setShowForm(false); fetchData(); }}
                    payment={editingPayment}
                    customers={customers}
                />
            )}
        </div>
    );
};

export default Payments;
